import { useState, type ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import TooltipIcon from "@/v2-ui/tooltip/TooltipIcon"
import Input from "@/v2-ui/input"
import Select from "@/v2-ui/select"
import Badge from "@/v2-backoffice-client/components/badge"
import Button from "@/v2-backoffice-client/components/button"
import Link from "@/v2-backoffice-client/components/link"
import TooltipLink from "@/v2-backoffice-client/components/link/TooltipLink"
import {
  FENERUM_ACCOUNT_BASE_URL,
  HUBSPOT_BASE_URL,
} from "@/v2-backoffice-client/constants"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import UsageFilterChips, {
  type UsageFilterChipOption,
} from "@/v2-backoffice-client/usage/UsageFilterChips"
import UsageChurnRiskBadge from "@/v2-backoffice-client/usage/scope/UsageChurnRiskBadge"
import UsageSeatBar from "@/v2-backoffice-client/usage/scope/UsageSeatBar"
import UsageScoreMeter from "@/v2-backoffice-client/usage/UsageScoreMeter"
import UsageInsightText from "@/v2-backoffice-client/usage/UsageInsightText"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import {
  CHURN_RISK_LABELS,
  type UsageInsight,
  type UsageInsightMention,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import {
  CHURN_LEVEL_CHART_COLORS,
  PLAY_KIND_LABELS,
  PLAY_TONE_LABELS,
  PLAY_TONE_ORDER,
  PLAYBOOK_CHAPTER_LABELS,
  PLAYBOOK_CHAPTER_LEADS,
  PLAYBOOK_CHAPTER_ORDER,
  type PlaybookChapterKey,
  PLAYBOOK_PREVIEW_CUSTOMERS,
  RENEWAL_SOON_DAYS,
} from "@/v2-backoffice-client/portfolio/portfolio.constants"
import {
  buildCustomerActivityLink,
  deadlineBadgeVariant,
  formatDeadline,
} from "@/v2-backoffice-client/portfolio/portfolio.utils"
import {
  derivePortfolioPlaybookSummary,
  groupPortfolioPlays,
  isUrgentPlayGroup,
  type PortfolioPlay,
  type PortfolioPlayGroup,
} from "@/v2-backoffice-client/portfolio/portfolio.plays.utils"
import { usageKpiGridClass } from "@/v2-backoffice-client/usage/report/usage.report.utils"

/**
 * Account-card orderings. "Urgency" is the derived default (tone → churn →
 * deadline → money → seats); the rest are the axes a CS person actually
 * licenses by on a given day.
 */
type PlaybookSort = "urgency" | "renewal" | "value" | "seats" | "name"

const PLAYBOOK_SORTS: { key: PlaybookSort; label: string }[] = [
  { key: "urgency", label: "By urgency" },
  { key: "renewal", label: "By renewal date" },
  { key: "value", label: "By value" },
  { key: "seats", label: "By seats" },
  { key: "name", label: "By name" },
]

function sortLabel(sort: PlaybookSort) {
  return PLAYBOOK_SORTS.find((option) => option.key === sort)?.label ?? ""
}

function sortGroups(groups: PortfolioPlayGroup[], sort: PlaybookSort) {
  if (sort === "urgency") return groups
  const sorted = [...groups]
  switch (sort) {
    case "renewal":
      return sorted.sort(
        (a, b) =>
          (a.deadline?.daysUntil ?? Number.MAX_SAFE_INTEGER) -
          (b.deadline?.daysUntil ?? Number.MAX_SAFE_INTEGER),
      )
    case "value":
      // Biggest 12-month books first; accounts without invoice history last.
      return sorted.sort((a, b) => (b.billed12M ?? -1) - (a.billed12M ?? -1))
    case "seats":
      return sorted.sort((a, b) => b.seatCount - a.seatCount)
    case "name":
      return sorted.sort((a, b) => a.customerName.localeCompare(b.customerName))
  }
}

const COPY_FEEDBACK_MS = 1500

function countAccounts(plays: PortfolioPlay[]) {
  return new Set(plays.map((play) => play.customerExternalId)).size
}

const TONES = Object.keys(PLAY_TONE_ORDER) as UsageInsight["tone"][]

type PlaybookChapter = {
  /** null = one flat list (an explicit sort order, not urgency). */
  key: PlaybookChapterKey | null
  groups: PortfolioPlayGroup[]
}

function chapterOf(group: PortfolioPlayGroup): PlaybookChapterKey {
  if (group.tone === "action") {
    return isUrgentPlayGroup(group) ? "act-now" : "watch"
  }
  return group.tone
}

/**
 * In urgency order the list reads as chapters (act now, keep an eye on,
 * openings, wins), each account under its most urgent tone. Under an
 * explicit sort the chapters would break the order the reader asked for,
 * so it is one list.
 */
function chapterGroups(
  groups: PortfolioPlayGroup[],
  sort: PlaybookSort,
): PlaybookChapter[] {
  if (sort !== "urgency") return [{ key: null, groups }]
  return PLAYBOOK_CHAPTER_ORDER.map((key) => ({
    key,
    groups: groups.filter((group) => chapterOf(group) === key),
  })).filter((chapter) => chapter.groups.length > 0)
}

export type PortfolioPlaybookProps = ComponentProps<"div"> & {
  /** Every customer's plays (already globally filtered), in urgency order. */
  plays: PortfolioPlay[]
}

// The global playbook: every customer's recommended plays, one card per
// account in urgency order — the week-planning tool. Deliberately
// STATELESS: there is no check-off. An item disappears when the underlying
// signal changes (the play worked) and CRM workflow stays in HubSpot.
function PortfolioPlaybook(props: PortfolioPlaybookProps) {
  const { plays, className, ...divProps } = props

  const [toneFilter, setToneFilter] = useState<UsageInsight["tone"] | null>(
    null,
  )
  const [search, setSearch] = useState("")
  const [showAll, setShowAll] = useState(false)
  const [sort, setSort] = useState<PlaybookSort>("urgency")
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // The play texts ARE the HubSpot note — copy them instead of retyping.
  function copyPlays(group: PortfolioPlayGroup) {
    const lines = [
      `${group.customerName} — ${CHURN_RISK_LABELS[group.churnLevel]}${
        group.billed12M != null && group.billed12M > 0
          ? ` · ${formatCompactAmount(group.billed12M, group.primaryCurrency)} billed 12 mo`
          : ""
      }${group.deadline ? ` · ${formatDeadline(group.deadline)}` : ""}`,
      ...group.plays.map(
        (play) => `• ${PLAY_KIND_LABELS[play.kind]}: ${play.text}`,
      ),
    ]
    void navigator.clipboard.writeText(lines.join("\n"))
    setCopiedId(group.customerExternalId)
    window.setTimeout(() => {
      setCopiedId((current) =>
        current === group.customerExternalId ? null : current,
      )
    }, COPY_FEEDBACK_MS)
  }

  const summary = derivePortfolioPlaybookSummary(plays)

  const searchTerm = search.trim().toLowerCase()
  const scoped = plays.filter(
    (play) =>
      searchTerm === "" || play.customerName.toLowerCase().includes(searchTerm),
  )
  const filtered =
    toneFilter == null
      ? scoped
      : scoped.filter((play) => play.tone === toneFilter)

  const groups = sortGroups(groupPortfolioPlays(filtered), sort)
  // Chapters are cut from the whole list, so a chapter's count is the real
  // one; each chapter then shows its first page until "Show all".
  const chapters = chapterGroups(groups, sort).map((chapter) => ({
    ...chapter,
    shown: showAll
      ? chapter.groups
      : chapter.groups.slice(0, PLAYBOOK_PREVIEW_CUSTOMERS),
  }))
  const hiddenGroupCount = chapters.reduce(
    (sum, chapter) => sum + chapter.groups.length - chapter.shown.length,
    0,
  )

  // Chips count accounts, like the cards they reveal and the tiles above.
  const toneOptions: UsageFilterChipOption<UsageInsight["tone"]>[] = [
    { label: `All (${formatNumber(countAccounts(scoped))})`, value: null },
    ...TONES.map((tone) => ({
      label: `${PLAY_TONE_LABELS[tone]} (${formatNumber(
        countAccounts(scoped.filter((play) => play.tone === tone)),
      )})`,
      value: tone,
    })),
  ]

  return (
    <div className={cn("usage-report", className)} {...divProps}>
      <PortfolioSectionHeader
        eyebrow="Playbook"
        title="What to do this week"
        lead="One card per account, most urgent first: this week's calls, then the watch list, then openings and wins; within each, more money at stake before less. A name opens the account; the copy button lifts its plays into a HubSpot note."
        info="Ordered by urgency: actions before opportunities before wins, then high churn risk first, then the nearest renewal or trial date, then more money at stake (billed the last 12 months) before less. The list derives fresh from the last 28 days of usage — an item clears when its signal does, and follow-up workflow lives in HubSpot. The global filter bar (churn, activity, customers) scopes this list too."
      >
        <UsageFilterChips
          options={toneOptions}
          value={toneFilter}
          onChange={(value) => setToneFilter(value)}
        />
        <div className="w-44">
          <Input
            type="search"
            size="xs"
            placeholder="Filter by customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-36 shrink-0">
          <Select
            value={sort}
            onValueChange={(value) => setSort(value as PlaybookSort)}
          >
            <Select.Trigger size="xs">
              <Select.Value placeholder="Sort" />
            </Select.Trigger>
            <Select.Content>
              {PLAYBOOK_SORTS.map((option) => (
                <Select.Item key={option.key} value={option.key}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </div>
      </PortfolioSectionHeader>

      <div className={usageKpiGridClass(4)}>
        <UsageReportKpi
          label="Accounts to act on"
          value={formatNumber(summary.actionAccounts)}
          tone={summary.actionAccounts > 0 ? "warning" : "default"}
          sub={[
            `at high churn risk, or renewing or ending a trial inside ${RENEWAL_SOON_DAYS} days`,
            summary.watchAccounts > 0
              ? `${formatNumber(summary.watchAccounts)} more to keep an eye on`
              : null,
          ]}
        />
        <UsageReportKpi
          label="Money at stake"
          value={
            summary.actionBilled > 0
              ? formatCompactAmount(summary.actionBilled, summary.currency)
              : "—"
          }
          sub={
            summary.actionBilled > 0
              ? "billed the last 12 months by the accounts to act on"
              : "no invoice history on the accounts to act on"
          }
        />
        <UsageReportKpi
          label="Openings"
          value={formatNumber(summary.openings)}
          sub={`expansion angles across ${formatNumber(summary.openingAccounts)} ${
            summary.openingAccounts === 1 ? "account" : "accounts"
          }`}
        />
        <UsageReportKpi
          label="Wins"
          value={formatNumber(summary.wins)}
          sub={`good stories across ${formatNumber(summary.winAccounts)} ${
            summary.winAccounts === 1 ? "account" : "accounts"
          }: a renewal to anchor, a reference to ask`}
        />
      </div>

      {groups.length === 0 ? (
        <p className="usage-report-empty">
          {plays.length === 0
            ? "No plays right now: the usage signals look healthy across the customers in scope."
            : "No account matches the filters."}
        </p>
      ) : (
        <div className="flex flex-col gap-8">
          {chapters.map((chapter) => (
            <UsageReportSection
              key={chapter.key ?? "all"}
              level="subsection"
              title={
                chapter.key == null
                  ? "All accounts"
                  : PLAYBOOK_CHAPTER_LABELS[chapter.key]
              }
              lead={
                chapter.key == null
                  ? `Sorted ${sortLabel(sort).toLowerCase()}.`
                  : PLAYBOOK_CHAPTER_LEADS[chapter.key]
              }
              aside={
                <span className="text-xs tabular-nums text-muted-foreground">
                  {formatNumber(chapter.groups.length)}{" "}
                  {chapter.groups.length === 1 ? "account" : "accounts"}
                  {chapter.shown.length < chapter.groups.length &&
                    `, ${formatNumber(chapter.shown.length)} shown`}
                </span>
              }
            >
              <div className="usage-report-grid items-start">
                {chapter.shown.map((group) => (
                  <PlaybookAccountCard
                    key={group.customerExternalId}
                    group={group}
                    copied={copiedId === group.customerExternalId}
                    onCopy={() => copyPlays(group)}
                  />
                ))}
              </div>
            </UsageReportSection>
          ))}

          {(hiddenGroupCount > 0 || showAll) && (
            <button
              type="button"
              className="self-start text-xs text-muted-foreground underline-offset-2 hover:underline"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll
                ? "Show fewer"
                : `Show all ${formatNumber(groups.length)} accounts (${formatNumber(hiddenGroupCount)} more)`}
            </button>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        The plays derive fresh from the last 28 days of usage: one disappears
        when its signal does, which is the sign it worked. There is nothing to
        tick off here; follow-up lives in HubSpot.
      </p>
    </div>
  )
}

type PlaybookAccountCardProps = {
  group: PortfolioPlayGroup
  copied: boolean
  onCopy(): void
}

const PLAY_TONE_DOT: Record<UsageInsight["tone"], string> = {
  action: "bg-amber-400",
  opportunity: "bg-teal-400",
  positive: "bg-green-500",
}

function buildMentionTo(mention: UsageInsightMention) {
  return `?v=user:${mention.userId}&user-view-tabs-tab=usage`
}

// One account: who, how it stands, what to do. The churn level colours the
// left edge so a column of cards scans by risk. The standing is pictures,
// not sentences: the money, the seat bar, the engagement meter, the trend.
// Each play is one line — the move in bold and the data behind it — with
// the full explanation one hover away, so a chapter of forty cards can be
// read down the left edge.
function PlaybookAccountCard(props: PlaybookAccountCardProps) {
  const { group, copied, onCopy } = props

  return (
    <UsageReportCard
      className="border-l-2"
      style={{
        borderLeftColor:
          group.churnLevel === "none"
            ? CHURN_LEVEL_CHART_COLORS.new
            : CHURN_LEVEL_CHART_COLORS[group.churnLevel],
      }}
      title={
        <span className="flex flex-wrap items-center gap-2">
          <Link
            to={buildCustomerActivityLink(group.customerExternalId)}
            iconSrc={null}
          >
            {group.customerName}
          </Link>
          <UsageChurnRiskBadge
            level={group.churnLevel}
            reasons={group.churnReasons}
          />
          {group.deadline != null &&
            group.deadline.daysUntil <= RENEWAL_SOON_DAYS && (
              <Badge variant={deadlineBadgeVariant(group.deadline)}>
                {formatDeadline(group.deadline)}
              </Badge>
            )}
        </span>
      }
      aside={
        <>
          <Button
            variant="ghost"
            size="xs"
            iconSrc={copied ? "check" : "copy"}
            title="Copy the plays as text — paste into a HubSpot note or task"
            onClick={onCopy}
          />
          <TooltipLink
            variant="ghost"
            size="xs"
            iconSrc="hubspot"
            title="Open the company in HubSpot"
            external
            to={`${HUBSPOT_BASE_URL}/${group.customerExternalId}`}
          />
          {group.billingAccountUuid && (
            <TooltipLink
              variant="ghost"
              size="xs"
              iconSrc="bank"
              title="Open the billing account in Fenerum"
              external
              to={`${FENERUM_ACCOUNT_BASE_URL}/${group.billingAccountUuid}/`}
            />
          )}
        </>
      }
    >
      <div className="playbook-standing">
        <div
          className="playbook-standing-cell"
          title="Billed the last 12 months — the money this account's signals put at stake"
        >
          <span className="playbook-standing-value">
            {group.billed12M != null && group.billed12M > 0
              ? formatCompactAmount(group.billed12M, group.primaryCurrency)
              : "—"}
          </span>
          <span className="playbook-standing-label">billed 12 months</span>
        </div>
        <div
          className="playbook-standing-cell playbook-standing-cell--wide"
          title="Every seat by how engaged it was in the last 28 days; grey is idle"
        >
          <UsageSeatBar bands={group.seatBands} />
          <span className="playbook-standing-label">
            {formatNumber(group.activeSeats)} of {formatNumber(group.seatCount)}{" "}
            seats active
          </span>
        </div>
        <div
          className="playbook-standing-cell"
          title="Average engagement score of the active seats, out of 100"
        >
          <span className="playbook-standing-value">
            {group.avgScore != null ? Math.round(group.avgScore) : "—"}
          </span>
          <UsageScoreMeter score={group.avgScore} />
          <span className="playbook-standing-label">engagement</span>
        </div>
        <div className="playbook-standing-cell">
          <UsageTrendBadge trendPct={group.trendPct} />
          <span className="playbook-standing-label">vs four weeks before</span>
        </div>
      </div>

      <ul className="playbook-plays">
        {group.plays.map((play) => (
          <li key={play.kind} className="playbook-play">
            <span
              aria-hidden
              className={cn("playbook-play-dot", PLAY_TONE_DOT[play.tone])}
            />
            <span className="playbook-play-label">
              {PLAY_KIND_LABELS[play.kind]}
            </span>
            <span className="playbook-play-brief">
              <UsageInsightText
                text={play.brief}
                mentions={play.mentions}
                buildTo={buildMentionTo}
              />
            </span>
            <TooltipIcon
              iconSrc="info"
              iconSize={13}
              iconClassName="shrink-0 text-muted-foreground/60"
              content={
                <span className="block max-w-[320px] px-3 py-2 text-xs font-normal leading-relaxed">
                  {play.text}
                </span>
              }
              contentSide="top"
              contentClassName="ui-tooltip-content--panel"
            />
          </li>
        ))}
      </ul>
    </UsageReportCard>
  )
}

export default PortfolioPlaybook
